import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../lib/supabase';
import { Tables } from '../lib/database.types';

const QUERY_KEY = 'projects';

export const useProjects = (options = {}) => {
  const { 
    isPublished = true, 
    type = null, 
    limit = 10, 
    offset = 0,
    language = 'fr'
  } = options;

  return useQuery({
    queryKey: [QUERY_KEY, { isPublished, type, limit, offset, language }],
    queryFn: async () => {
      let query = supabase
        .from(Tables.PROJECTS)
        .select(`
          *,
          translations:projects_translations(title, description, language_code),
          technologies:project_technologies(
            technology:technologies(id, name, logo_url, category)
          )
        `)
        .order('display_order', { ascending: true })
        .range(offset, offset + limit - 1);

      if (isPublished !== null) {
        query = query.eq('is_published', isPublished);
      }

      if (type) {
        query = query.eq('type', type);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      return data.map(project => ({
        ...project,
        title: project.translations?.find(t => t.language_code === language)?.title || project.translations?.[0]?.title,
        description: project.translations?.find(t => t.language_code === language)?.description || project.translations?.[0]?.description,
      }));
    },
  });
};

export const useProject = (slug) => {
  return useQuery({
    queryKey: [QUERY_KEY, slug],
    queryFn: async () => {
      const { data, error } = await supabase
        .from(Tables.PROJECTS)
        .select(`
          *,
          translations:projects_translations(title, description, language_code),
          images:project_images(id, image_url, alt_text, display_order),
          technologies:project_technologies(
            technology:technologies(id, name, logo_url, category)
          )
        `)
        .eq('slug', slug)
        .single();

      if (error) throw error;
      return data;
    },
    enabled: !!slug,
  });
};

export const useCreateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (project) => {
      const { data, error } = await supabase
        .from(Tables.PROJECTS)
        .insert(project)
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useUpdateProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ id, ...updates }) => {
      const { data, error } = await supabase
        .from(Tables.PROJECTS)
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', id)
        .select();

      if (error) throw error;
      return data?.[0] || updates;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export const useDeleteProject = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id) => {
      const { error } = await supabase
        .from(Tables.PROJECTS)
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY] });
    },
  });
};

export default useProjects;
